import { sendRequest } from "@infrastructure/api/api";
import { InvitationResponse } from "@features/invitations/types/invitation";
import { DepartmentResponse } from "@features/invitations/types/departmentResponse";
import { RoleResponse } from "@features/invitations/types/roleResponse";
import ApiResponse from "@core/types/apiResponse";
import { ApiMessageResponse } from "@core/types/apiMessageResponse";
import { SendInvitationRequest } from "../../../invitations/types/sendInvitationRequest";
import { EntityResponse } from "@features/invitations/types/entity";
import { getApiScopeFromValue } from "@features/invitations/utils/getScopeFromParam";

const toQueryParams = (params: Record<string, any>): string => {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        query.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    });

    return query.toString();
};

const withQuery = (path: string, params: Record<string, any>) => {
    const query = toQueryParams(params);
    return query ? `${path}?${query}` : path;
};


/**
 * Fetches church invitations based on the provided parameters.
 *
 * @param params - An object containing query parameters for fetching invitations.
 *   - `scope`: The scope of the invitation query (not directly used in the request).
 *   - `scope_ref_id`: The church ID to be used in the API endpoint.
 *   - Additional parameters are included as query parameters in the request.
 * @returns A promise that resolves to an `ApiResponse` containing `InvitationResponse` data.
 */
export const fetchChurchInvitations = async (params: any): Promise<ApiResponse<InvitationResponse>> => {
    // Extract relevant parameters for the request
    const { scope: _scope, scope_ref_id, ...fetchParams } = params;

    let churchId = scope_ref_id;


    const response = await sendRequest<any, InvitationResponse>('GET', withQuery(`/church/${churchId}/invite/query`, fetchParams));
    return response;
};


/**
 * Fetches invitations for a specific district based on provided parameters.
 *
 * @param params - An object containing request parameters. Must include `scope_ref_id` (district ID) and may include additional query parameters.
 * @returns A promise that resolves to an `ApiResponse` containing the invitation response data.
 *
 * @remarks
 * - The function extracts `scope` and `scope_ref_id` from the input parameters.
 * - Remaining parameters are used as query parameters in the request.
 * - Sends a GET request to `/district/{districtId}/invite/query`.
 */
export const fetchDistrictInvitations = async (params: any): Promise<ApiResponse<InvitationResponse>> => {
    // Extract relevant parameters for the request
    const { scope: _scope, scope_ref_id, ...fetchParams } = params;

    let districtId = scope_ref_id;

    const response = await sendRequest<any, InvitationResponse>('GET', withQuery(`/district/${districtId}/invite/query`, fetchParams));
    return response;
};


/**
 * Fetches invitation data for a specific nation based on provided parameters.
 *
 * @param params - An object containing query parameters for the request. 
 *   - `scope`: The scope of the invitation query (not used in the request).
 *   - `scope_ref_id`: The nation ID to be used in the API endpoint.
 *   - Additional parameters are included as query parameters in the request.
 * @returns A promise that resolves to an `ApiResponse` containing the invitation response data.
 */
export const fetchNationInvitations = async (params: any): Promise<ApiResponse<InvitationResponse>> => {
    // Extract relevant parameters for the request
    const { scope: _scope, scope_ref_id, ...fetchParams } = params;

    let nationId = scope_ref_id;

    const response = await sendRequest<any, InvitationResponse>('GET', withQuery(`/nation/${nationId}/invite/query`, fetchParams));
    return response;
};

/**
 * Fetches information about a church based on the provided query parameters.
 *
 * @param params - An object containing query parameters to filter the church information.
 * @returns A promise that resolves to an `ApiResponse` containing the `EntityResponse` data.
 */
export const fetchChurchInfo = async (params: any): Promise<ApiResponse<EntityResponse>> => {
    const response = await sendRequest<any, EntityResponse>('GET', withQuery(`/church/query`, params));
    return response;
}

/**
 * Fetches district information based on the provided query parameters.
 *
 * @param params - An object containing query parameters for the district search.
 * @returns A promise that resolves to an `ApiResponse` containing an `EntityResponse` with the district information.
 */
export const fetchDistrictInfo = async (params: any): Promise<ApiResponse<EntityResponse>> => {
    const response = await sendRequest<any, EntityResponse>('GET', withQuery(`/district/query`, params));
    return response;
}

/**
 * Fetches information about a nation based on the provided query parameters.
 *
 * @param params - An object containing query parameters to filter the nation information.
 * @returns A promise that resolves to an `ApiResponse` containing an `EntityResponse` with the nation data.
 */
export const fetchNationInfo = async (params: any): Promise<ApiResponse<EntityResponse>> => {
    const response = await sendRequest<any, EntityResponse>('GET', withQuery(`/nation/query`, params));
    return response;
}

/**
 * Fetches a list of departments based on the provided query parameters.
 * When scope and scope_ref_id are provided, uses the scoped endpoint (church/, district/, nation/).
 *
 * @param params - Query params. May include scope and scope_ref_id for scoped endpoint.
 * @returns A promise that resolves to an `ApiResponse` containing the department response data.
 */
export const fetchDepartments = async (params: any): Promise<ApiResponse<DepartmentResponse>> => {
    const { scope, scope_ref_id, ...queryParams } = params;
    const scopeRefId = scope_ref_id != null ? Number(scope_ref_id) : NaN;
    const apiScope = getApiScopeFromValue(String(scope || ''));
    const hasScope = apiScope && !Number.isNaN(scopeRefId) && scopeRefId > 0;

    let path: string;
    if (hasScope && apiScope === 'church') {
        path = `church/${scopeRefId}/department/query`;
    } else if (hasScope && apiScope === 'district') {
        path = `district/${scopeRefId}/department/query`;
    } else if (hasScope && apiScope === 'nation') {
        path = `nation/${scopeRefId}/department/query`;
    } else {
        path = 'department/query';
    }
    const response = await sendRequest<any, DepartmentResponse>('GET', withQuery(`/${path}`, queryParams));
    return response;
};

/**
 * Fetches system roles from the server based on the provided query parameters.
 *
 * @param params - An object containing query parameters to filter or paginate the roles.
 * @returns A promise that resolves to an `ApiResponse` containing the `RoleResponse` data.
 *
 * @example
 * ```typescript
 * const roles = await fetchRoles({ page: 1, pageSize: 10 });
 * ```
 */
export const fetchRoles = async (params: any): Promise<ApiResponse<RoleResponse>> => {
    const response = await sendRequest<any, RoleResponse>('GET', withQuery(`/user/roles/query`, params));
    return response;
};

/**
 * Sends an invitation to a specified entity and entity ID using the provided invitation data.
 *
 * @param data - The invitation request payload containing scope and reference ID.
 * @returns A promise that resolves to an API response containing the invitation response data.
 *
 * @example
 * ```typescript
 * const response = await createInvitation({
 *   scope: 'church',
 *   scope_ref_id: '123',
 *   // other invitation data
 * });
 * ```
 */
export const createInvitation = async (
    scope: string,
    scopeRefId: number,
    data: SendInvitationRequest
): Promise<ApiResponse<ApiMessageResponse>> => {
    const entity = getApiScopeFromValue(scope) || scope.toLowerCase();

    const response = await sendRequest<SendInvitationRequest, ApiMessageResponse>(
        'POST',
        `/${entity}/${scopeRefId}/invite/send`,
        data
    );
    return response;
};

/**
 * Cancels an invitation for a specific entity and entity reference ID.
 *
 * @param id - The unique identifier of the invitation to cancel.
 * @param data - An object containing the scope (entity type) and scope_ref_id (entity reference ID).
 * @returns A promise that resolves to an ApiResponse containing the result of the cancellation.
 */
export const cancelInvitation = async (id: number, data: { scope: string; scope_ref_id: number }): Promise<ApiResponse<ApiMessageResponse>> => {
    let entity = getApiScopeFromValue(data.scope) || data.scope.toLowerCase();
    let entityId = data.scope_ref_id;

    const response = await sendRequest<{ scope: string; scope_ref_id: number }, ApiMessageResponse>('POST', `/${entity}/${entityId}/invite/cancel/${id}`, data);
    return response;
};


/**
 * Resends an invitation for a specific entity and entity reference ID.
 *
 * @param id - The ID of the invitation to resend.
 * @param data - An object containing the scope (entity type) and scope_ref_id (entity reference ID).
 * @returns A promise that resolves to an ApiResponse containing the result of the resend operation.
 */
export const resendInvitation = async (id: number, data: { scope: string; scope_ref_id: number }): Promise<ApiResponse<ApiMessageResponse>> => {
    let entity = getApiScopeFromValue(data.scope) || data.scope.toLowerCase();
    let entityId = data.scope_ref_id;

    const response = await sendRequest<{ scope: string; scope_ref_id: number }, ApiMessageResponse>('POST', `/${entity}/${entityId}/invite/resend/${id}`, data);

    return response;
};

/**
 * Accepts an invitation by its ID.
 *
 * Sends a POST request to the `/invite/accept/:id` endpoint to accept the invitation.
 *
 * @param id - The unique identifier of the invitation to accept.
 * @returns A promise that resolves to an `ApiResponse` containing the server's response.
 */
export const acceptInvitation = async (id: number): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `/user/invite/accept/${id}`);
    return response;
}

/**
 * Sends a request to reject an invitation by its ID.
 *
 * @param id - The unique identifier of the invitation to reject.
 * @returns A promise that resolves to an ApiResponse containing the result of the rejection operation.
 */
export const rejectInvitation = async (id: number): Promise<ApiResponse<ApiMessageResponse>> => {
    const response = await sendRequest<any, ApiMessageResponse>('POST', `/user/invite/reject/${id}`);
    return response;
}
